import React, { useContext, useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { Navigate, useLocation } from "react-router-dom";
import { StatusEnum, ThemeColorsEnum } from "../../constants/enums";
import {
  AuthContext,
  LoadingContext,
  ModalContext,
} from "../../context/contextProvider";

import { CSVLink } from "react-csv";
import AddButton from "../../components/buttons/AddButton";
import LoadingButton from "../../components/buttons/LoadingButton";
import Card from "../../components/cards/Card";
import EmptyDataBox from "../../components/dataTables/EmptyDataBox";
import NSDataTable from "../../components/dataTables/NSDataTable";
import NSDataTablePagination from "../../components/dataTables/NSDataTablePagination";
import { AppRoutes } from "../../constants/appRoutes";
import Constants from "../../constants/constants";
import useServiceOrdersDataTable from "../../hooks/useServiceOrdersDataTable";
import { Search } from "../../models/searchFilterRequest";
import ServiceOrderFilterRequest from "../../models/serviceOrderFilterRequest";
import Services from "../../services/services";
import Page from "../Page";
import ServiceOrderDetails from "./ServiceOrderDetails";

const ServiceOrdersPage = ({
  breadcrumbs,
  completed,
  dashboardType,
  title,
  ...otherProps
}) => {
  const { loading, setLoading } = useContext(LoadingContext);
  const { showModal } = useContext(ModalContext);
  const { currentClient, branchModules, cache, setCache } =
    useContext(AuthContext);
  const dataTableInfo = useServiceOrdersDataTable(
    handleDeleted,
    handleSaved,
    handleMoved
  );

  // ----- start parsing page cache
  let _currentPage = 1;
  let _pageSize = Constants.defaults.rowsPerPage;
  let _searchValue = "";
  let _sortOptions = {
    column: dataTableInfo.defaultSortColumn(completed),
    direction: dataTableInfo.defaultSortOrder(completed),
  };

  if (!completed) {
    _currentPage = cache?.serviceOrdersActive?.currentPage ?? 1;
    _pageSize =
      cache?.serviceOrdersActive?.pageSize ?? Constants.defaults.rowsPerPage;
    _searchValue = cache?.serviceOrdersActive?.search ?? "";
    _sortOptions = {
      column:
        cache?.serviceOrdersActive?.sortColumn ??
        dataTableInfo.defaultSortColumn(completed),
      direction:
        cache?.serviceOrdersActive?.sortDirection ??
        dataTableInfo.defaultSortOrder(completed),
    };
  } else {
    _currentPage = cache?.serviceOrdersActive?.currentPage ?? 1;
    _pageSize =
      cache?.serviceOrdersActive?.pageSize ?? Constants.defaults.rowsPerPage;
    _searchValue = cache?.serviceOrdersCompleted?.search ?? "";
    _sortOptions = {
      column:
        cache?.serviceOrdersCompleted?.sortColumn ??
        dataTableInfo.defaultSortColumn(completed),
      direction:
        cache?.serviceOrdersCompleted?.sortDirection ??
        dataTableInfo.defaultSortOrder(completed),
    };
  }
  // ----- end parsing page cache
  const [dashboardTypeState, setDashboardTypeState] = useState(dashboardType);
  // data-tables - start

  const [currentPage, setCurrentPage] = useState(_currentPage);
  const [pageSize, setPageSize] = useState(_pageSize);
  const [paginationData, setPaginationData] = useState({
    from: null,
    pages: null,
    records: null,
    to: null,
  });
  const [searchValue, setSearchValue] = useState(_searchValue);
  const [sortOptions, setSortOptions] = useState(_sortOptions);
  // data-tables - end

  const [orders, setOrders] = useState([]);

  const { state } = useLocation();

  useEffect(() => {
    saveDatatableCache(currentPage, pageSize, searchValue, sortOptions);
    getOrdersAsync((currentPage - 1) * pageSize, pageSize, searchValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentClient,
    currentPage,
    pageSize,
    searchValue,
    sortOptions,
    state?.filter,
  ]);

  useEffect(() => {
    setDashboardTypeState(null);
  }, [currentClient]);

  const getOrdersAsync = async (
    start = 0,
    take = Constants.defaults.rowsPerPage,
    searchText = null,
    dashboardFilter = dashboardTypeState,
    forceReload = false
  ) => {
    // filter
    const filter = new ServiceOrderFilterRequest();
    filter.search = new Search(searchText);
    filter.start = start;
    filter.length = take;
    filter.dashboardType = !!forceReload ? null : dashboardFilter ?? null;
    filter.status =
      state?.filter?.status ??
      (completed ? StatusEnum.completed : StatusEnum.active);
    filter.order[0].column = sortOptions.column;
    filter.order[0].dir = sortOptions.direction;
    filter.completedOnFrom = state?.filter?.completedOnFrom;
    filter.completedOnTo = state?.filter?.completedOnTo;

    setLoading(true);
    const response = await Services.api.serviceOrders.getServiceOrders(
      JSON.stringify(!!forceReload ? filter : state?.filter ?? filter)
    );
    setLoading(false);

    if (!response.ok)
      return Services.utility.toast(
        "Could not fetch the orders",
        ThemeColorsEnum.error
      );

    setOrders(response?.data?.data);
    setPaginationData({
      from: (currentPage - 1) * pageSize + 1,
      pages: response.data?.pagesTotal,
      records: response.data?.recordsTotal,
      to:
        currentPage * pageSize > response.data?.recordsTotal
          ? response.data?.recordsTotal
          : currentPage * pageSize,
    });
  };

  function handleDeleted(serviceOrder) {
    setOrders(orders.filter((x) => x.id !== serviceOrder.id));
  }

  function handleMoved(serviceOrder) {
    setOrders(orders.filter((x) => x.id !== serviceOrder.id));
  }

  const handlePageChange = async (page) => {
    setCurrentPage(page);
  };

  const handleRefresh = async () =>
    await getOrdersAsync(0, Constants.defaults.rowsPerPage, null, null, true);

  const handleRowClick = (event, row) => {
    event.stopPropagation();
    // opend the details modal
    showModal(
      `Service Order #${row.id}`,
      <ServiceOrderDetails serviceOrder={row} />,
      [
        {
          color: ThemeColorsEnum.quaternary,
          title: "Close",
        },
      ]
    );
  };

  const handleRowsPerPageChange = (newPerPage, currentPage) => {
    setCurrentPage(1);
    setPageSize(newPerPage);
  };

  function handleSaved(serviceOrder) {
    insertOrUpdateWorkOrder(serviceOrder);
  }

  const handleSearch = (searchValue) => {
    setCurrentPage(1);
    setSearchValue(searchValue);

    // ----- start updating the page cache
    let newCache = { ...cache };
    if (!completed) newCache.serviceOrdersActive.search = searchValue;
    else newCache.serviceOrdersCompleted.search = searchValue;

    setCache(newCache);
    // ----- end updating cache
  };

  const handleSort = (column, direction) => {
    setCurrentPage(1);
    setSortOptions({ column: column, direction: direction });
  };

  const insertOrUpdateWorkOrder = (serviceOrder) => {
    const index = orders.findIndex((x) => x.id === serviceOrder?.id);

    if (index >= 0) {
      // replace the existing item in the array
      orders[index] = serviceOrder;
      setOrders([...orders]);
    } else {
      // append the new item to the array
      setOrders([...orders, serviceOrder]);
    }
  };

  const saveDatatableCache = (
    currentPage,
    pageSize,
    searchValue,
    sortOptions
  ) => {
    let newCache = { ...cache };
    if (!completed)
      newCache.serviceOrdersActive = {
        currentPage: currentPage,
        pageSize: pageSize,
        search: searchValue,
        sortColumn: sortOptions?.column,
        sortDirection: sortOptions?.direction,
      };
    else
      newCache.serviceOrdersCompleted = {
        currentPage: currentPage,
        pageSize: pageSize,
        search: searchValue,
        sortColumn: sortOptions?.column,
        sortDirection: sortOptions?.direction,
      };

    setCache(newCache);
  };

  const DataTableButtons = (
    <>
      <AddButton to={AppRoutes.serviceOrders_create} />
      <CSVLink
        className="btn btn-secondary me-1"
        data={orders}
        headers={[
          { label: "Title/Ticket #", key: "title" },
          { label: "Customer", key: "customerDisplayName" },
          { label: "Notes", key: "notes" },
          { label: "Category", key: "categoryName" },
          { label: "Assigned to", key: "assignedToNames" },
          { label: "Status", key: "orderStatus" },
          { label: "Due Date", key: "dueDateFormatted" },
          { label: "Entered on", key: "createdDateFormatted" },
        ]}
        filename={`service-orders-${Services.utility.formatISODate(
          new Date()
        )}.csv`}
        target="_blank"
      >
        Export to Excel
      </CSVLink>
      <LoadingButton
        color={ThemeColorsEnum.quaternary}
        loading={loading}
        onClick={handleRefresh}
      >
        Refresh
      </LoadingButton>
    </>
  );

  if (!branchModules?.serviceOrders)
    return (
      <Navigate
        to={{
          pathname: AppRoutes.forbidden,
        }}
      />
    );

  return (
    <Page breadcrumbs={breadcrumbs} title={title}>
      <Row>
        <Col>
          <Card>
            <Card.Body className="overflow-auto">
              <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                {/* **** BOOTSTRAP PAGINATION **** */}
                <NSDataTablePagination
                  from={paginationData?.from}
                  lastPage={paginationData?.pages}
                  page={currentPage}
                  pageSize={pageSize}
                  total={paginationData?.records}
                  to={paginationData?.to}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(newPerPage) =>
                    handleRowsPerPageChange(newPerPage, currentPage)
                  }
                />
                {/* **** DATA TABLE **** */}
                <NSDataTable
                  buttons={DataTableButtons}
                  className="dataTable table-no-more"
                  // dense
                  columns={dataTableInfo.columns}
                  conditionalRowStyles={dataTableInfo.conditionalRowStyles}
                  data={orders}
                  emptyDataComponent={<EmptyDataBox />}
                  onRowClick={handleRowClick}
                  onSearch={handleSearch}
                  onSort={handleSort}
                  paginate
                  search
                  searchValue={searchValue}
                  sortBy={sortOptions.column}
                  sortDirection={sortOptions.direction}
                />
                {/* **** BOOTSTRAP PAGINATION **** */}
                <NSDataTablePagination
                  from={paginationData?.from}
                  lastPage={paginationData?.pages}
                  page={currentPage}
                  pageSize={pageSize}
                  showRecordsInfo
                  total={paginationData?.records}
                  to={paginationData?.to}
                  onPageChange={handlePageChange}
                  onPageSizeChange={(newPerPage) =>
                    handleRowsPerPageChange(newPerPage, currentPage)
                  }
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Page>
  );
};

export default ServiceOrdersPage;
